import { Box, Divider, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  root: {
    minHeight: "100vh",
    background: "var(--background-color)",
    color: "var(--text-color)",
    padding: "40px 24px",
    maxWidth: 860,
    margin: "0 auto",
  },
  section: {
    marginTop: 28,
  },
  subsection: {
    marginTop: 16,
  },
  highlight: {
    background: "var(--list-item-sel-color, #f0f4ff)",
    borderLeft: "4px solid #3f51b5",
    padding: "10px 16px",
    borderRadius: 4,
    margin: "12px 0",
  },
  contact: {
    marginTop: 8,
    lineHeight: 1.8,
  },
}));

export default function TermsOfService() {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <Typography variant="h4" gutterBottom>
        Terms of Service
      </Typography>
      <Typography variant="body2" style={{ color: "#808080" }}>
        Effective Date: April 19, 2026 &nbsp;|&nbsp; Last Updated: April 19, 2026
      </Typography>
      <Divider style={{ margin: "16px 0" }} />

      {/* 1 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>1. Acceptance of Terms</Typography>
        <Typography variant="body2">
          By accessing or using the QLearn platform ("Service") operated by Medlearnity
          ("Company," "we," "us," or "our"), you agree to be bound by these Terms of Service
          ("Terms"). If you do not agree, do not use the Service.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          These Terms apply to all visitors, users, and others who access or use the Service.
        </Typography>
      </Box>

      {/* 2 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>2. Description of Service</Typography>
        <Typography variant="body2">
          QLearn is a medical education platform designed to help healthcare students and
          professionals prepare for licensing examinations through adaptive quizzes, study
          tools, and performance analytics. The Service is accessible at
          https://www.medlearnity.com and via our mobile applications.
        </Typography>
      </Box>

      {/* 3 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>3. Eligibility</Typography>
        <Typography variant="body2">
          You must be at least 18 years old to use the Service. By using the Service, you
          represent that you are 18 or older and have the legal capacity to enter into these Terms.
        </Typography>
      </Box>

      {/* 4 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>4. Account Registration</Typography>
        <Typography variant="body2">To access features of the Service, you must create an account. You agree to:</Typography>
        <ul>
          <li><Typography variant="body2">Provide accurate and complete registration information</Typography></li>
          <li><Typography variant="body2">Maintain the security of your password</Typography></li>
          <li><Typography variant="body2">Notify us immediately of any unauthorized account access</Typography></li>
          <li><Typography variant="body2">Accept responsibility for all activities that occur under your account</Typography></li>
        </ul>
      </Box>

      {/* 5 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>5. SMS Communications</Typography>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.1 Consent to Receive SMS</Typography>
          <Typography variant="body2">
            By providing your phone number and enabling SMS in your account settings, you consent
            to receive SMS messages from Medlearnity (short name: QLearn) for the following
            transactional purposes:
          </Typography>
          <ul>
            <li><Typography variant="body2">One-time passcodes (OTP) for phone number verification</Typography></li>
            <li><Typography variant="body2">One-time passcodes for account login authentication</Typography></li>
          </ul>
          <Typography variant="body2"><strong>We do not send promotional or marketing SMS messages.</strong></Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.2 Message Frequency</Typography>
          <Typography variant="body2">
            Messages are sent only in response to user-initiated actions (e.g., requesting a
            verification code). You will not receive recurring automated marketing messages.
          </Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.3 Message and Data Rates</Typography>
          <Typography variant="body2">Standard message and data rates may apply. Contact your mobile carrier for details.</Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.4 Opt-Out</Typography>
          <Typography variant="body2">You may opt out of SMS communications at any time by:</Typography>
          <ul>
            <li><Typography variant="body2">Replying <strong>STOP</strong> to any SMS from us</Typography></li>
            <li><Typography variant="body2">Disabling SMS in your QLearn account Preferences</Typography></li>
          </ul>
          <Typography variant="body2">
            Upon sending STOP, you will receive a confirmation and no further SMS will be sent
            unless you re-enable SMS in your account settings or reply <strong>START</strong>.
          </Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.5 Re-Subscribing</Typography>
          <Typography variant="body2">
            To re-enable SMS after opting out, reply <strong>START</strong> to any previous SMS from us
            or enable SMS in your account Preferences.
          </Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.6 Help</Typography>
          <Typography variant="body2">Reply <strong>HELP</strong> to any SMS from QLearn for assistance, or contact:</Typography>
          <ul>
            <li><Typography variant="body2">Email: support@medlearnity.com</Typography></li>
            <li><Typography variant="body2">Phone: +1 (631) 540-2499</Typography></li>
          </ul>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.7 Supported Carriers</Typography>
          <Typography variant="body2">
            QLearn SMS is supported on all major US carriers including AT&amp;T, Verizon, T-Mobile,
            Sprint, Boost, Cricket, Metro by T-Mobile, US Cellular, and others. Carrier support
            may vary for MVNO carriers.
          </Typography>
        </Box>
      </Box>

      {/* 6 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>6. Acceptable Use</Typography>
        <Typography variant="body2">You agree not to:</Typography>
        <ul>
          <li><Typography variant="body2">Use the Service for any unlawful purpose</Typography></li>
          <li><Typography variant="body2">Attempt to gain unauthorized access to any part of the Service</Typography></li>
          <li><Typography variant="body2">Interfere with or disrupt the integrity or performance of the Service</Typography></li>
          <li><Typography variant="body2">Share your account credentials with others</Typography></li>
          <li><Typography variant="body2">Use automated tools to scrape or harvest content from the Service</Typography></li>
          <li><Typography variant="body2">Impersonate any person or entity</Typography></li>
        </ul>
      </Box>

      {/* 7 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>7. Intellectual Property</Typography>
        <Typography variant="body2">
          All content on the Service — including text, graphics, question banks, explanations,
          UI design, and software — is the property of Medlearnity and protected by copyright,
          trademark, and other intellectual property laws.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          You are granted a limited, non-exclusive, non-transferable license to access and use
          the Service for your personal, non-commercial educational purposes.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          You may not reproduce, distribute, modify, or create derivative works from any content
          without our prior written consent.
        </Typography>
      </Box>

      {/* 8 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>8. Subscriptions and Payments</Typography>
        <Typography variant="body2">Certain features of the Service require a paid subscription. By subscribing, you agree to:</Typography>
        <ul>
          <li><Typography variant="body2">Pay all applicable fees as described on the pricing page</Typography></li>
          <li><Typography variant="body2">Authorize us to charge your payment method on a recurring basis (if applicable)</Typography></li>
          <li><Typography variant="body2">Our refund policy as described at the time of purchase</Typography></li>
        </ul>
        <Typography variant="body2">Web payments are processed securely via Stripe; in-app subscriptions on iOS are processed by Apple through In-App Purchase. We do not store your full payment card details.</Typography>
      </Box>

      {/* 9 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>9. Termination</Typography>
        <Typography variant="body2">
          We reserve the right to suspend or terminate your account at our sole discretion,
          without notice, for conduct that we believe violates these Terms or is harmful to
          other users, us, or third parties.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          You may terminate your account at any time by contacting support@medlearnity.com.
        </Typography>
      </Box>

      {/* 10 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>10. Disclaimer of Warranties</Typography>
        <Typography variant="body2">
          The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties
          of any kind, either express or implied, including but not limited to implied warranties
          of merchantability, fitness for a particular purpose, or non-infringement.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          We do not warrant that the Service will be uninterrupted, error-free, or free of viruses.
        </Typography>
      </Box>

      {/* 11 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>11. Limitation of Liability</Typography>
        <Typography variant="body2">
          To the maximum extent permitted by law, Medlearnity shall not be liable for any
          indirect, incidental, special, consequential, or punitive damages, including loss of
          profits, data, or goodwill, arising out of or in connection with your use of the Service.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          Our total liability to you for any claims arising from use of the Service shall not
          exceed the amount you paid to us in the twelve (12) months preceding the claim.
        </Typography>
      </Box>

      {/* 12 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>12. Governing Law</Typography>
        <Typography variant="body2">
          These Terms shall be governed by and construed in accordance with the laws of the
          State of New York, without regard to its conflict of law provisions.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          Any disputes arising under these Terms shall be subject to the exclusive jurisdiction
          of the state and federal courts located in New York County, New York.
        </Typography>
      </Box>

      {/* 13 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>13. Changes to Terms</Typography>
        <Typography variant="body2">
          We reserve the right to modify these Terms at any time. We will provide notice of
          material changes by updating the "Last Updated" date and, where appropriate, notifying
          you by email. Continued use of the Service after changes constitutes acceptance.
        </Typography>
      </Box>

      {/* 14 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>14. Contact Us</Typography>
        <Typography variant="body2" className={classes.contact}>
          <strong>Medlearnity</strong><br />
          15608 Riverside Dr W, Apt 5F<br />
          New York, NY 10032<br />
          United States<br />
          Email: support@medlearnity.com<br />
          Phone: +1 (631) 540-2499<br />
          Website: https://www.medlearnity.com
        </Typography>
      </Box>

      <Box style={{ marginTop: 40, marginBottom: 16 }}>
        <Divider />
      </Box>
    </Box>
  );
}
